import { Dialog, DialogContent, Backdrop } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import icBack from 'Assets/img/ic_back_24_inverse@2x.png';
import icDownload from 'Assets/img/ic_download_24_inverse@2x.png';
import { useSelector } from 'react-redux';
import { koDateTimeFormat } from 'Util/DateUtils';
import { useIntl } from 'react-intl';
import { saveAs } from 'file-saver';
import icArrowLeftDisabled from 'Assets/img/ic_arrow_left_24_disabled@2x.png';
import icArrowRightInverse from 'Assets/img/ic_arrow_right_24_inverse@2x.png';
import BasicIconButton from 'Components/Button/BasicIconButton';
import { getFileName } from 'Util/DataUtils';

const ImageDialog = ({ modal = false, onClose = null, images = [], initIndex = -1, date, isMine }) => {
  const [index, setIndex] = useState(-1);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const { user } = useSelector(state => state.authUser);
  const { locale } = useSelector(state => state.settings.locale);
  const { formatMessage } = useIntl();

  const updateDimensions = () => {
    setWindowHeight(window.innerHeight);
  };

  useEffect(() => {
    if (modal) {
      setIndex(initIndex);
    }
  }, [modal, initIndex]);

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const userName = useMemo(() => {
    if (!isMine) {
      return formatMessage({ id: 'customer.support.chat.default.message' });
    }
    if (user.isStaff) {
      return user.partner.materialBuyer.staffs.name;
    } else {
      return user.name;
    }
  }, [user, isMine, formatMessage]);

  const handleKeyDown = useCallback(
    e => {
      if (!modal) return;

      switch (e.key) {
        case 'ArrowLeft':
          if (index > 0) {
            setIndex(index - 1);
          }
          break;
        case 'ArrowRight':
          if (index < images.length - 1) {
            setIndex(index + 1);
          }
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    },
    [index, modal, images, onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  if (!images || images.length === 0) return <></>;
  return (
    <Dialog
      fullScreen={true}
      open={modal}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      PaperProps={{
        style: {
          backgroundColor: 'transparent',
          transition: 'none',
          boxShadow: 'none',
        },
      }}
      slots={{
        backdrop: Backdrop,
      }}
      slotProps={{
        backdrop: {
          style: {
            backgroundColor: 'var(--Surface-Inverse-Inverse, #2D2D2D)',
          },
        },
      }}
    >
      <DialogContent style={{ padding: 0 }}>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '48px',
            display: 'flex',
          }}
        >
          <img className='cursor-pointer no-drag' src={icBack} alt='back' style={{ width: '24px', height: '24px', margin: '12px 16px' }} onClick={onClose} />
          <div className='customer-support-chat-image-dialog-text-wrapper'>
            <div className='customer-support-chat-image-dialog-user-name'>{userName}</div>
            <div className='customer-support-chat-image-dialog-date'>{koDateTimeFormat(date, locale)}</div>
          </div>
          <img
            className='cursor-pointer no-drag'
            src={icDownload}
            alt='download'
            style={{ width: '24px', height: '24px', margin: '12px 16px' }}
            onClick={() => saveAs(images[index], getFileName(images[index]))}
          />
        </div>
        {images.length !== 1 && (
          <>
            <BasicIconButton
              style={{
                position: 'fixed',
                top: '50%',
                left: 16,
                borderRadius: '9999px',
                background: 'var(--Color-Alpha-Black-30, rgba(0, 0, 0, 0.24))',
              }}
              type='lineless'
              size='large'
              onClick={() => setIndex(prev => prev - 1)}
              disabled={index === 0}
            >
              {index > 0 ? (
                <img src={icArrowRightInverse} alt='arrow left' style={{ width: '24px', height: '24px', transform: 'rotate(180deg)' }} />
              ) : (
                <img src={icArrowLeftDisabled} alt='arrow left disabled' style={{ width: '24px', height: '24px' }} />
              )}
            </BasicIconButton>
            <BasicIconButton
              style={{
                position: 'fixed',
                top: '50%',
                right: 16,
                borderRadius: '9999px',
                background: 'var(--Color-Alpha-Black-30, rgba(0, 0, 0, 0.24))',
              }}
              type='lineless'
              size='large'
              onClick={() => setIndex(prev => prev + 1)}
              disabled={index === images.length - 1}
            >
              {index < images.length - 1 ? (
                <img src={icArrowRightInverse} alt='arrow right' style={{ width: '24px', height: '24px' }} />
              ) : (
                <img src={icArrowLeftDisabled} alt='arrow right disabled' style={{ width: '24px', height: '24px', transform: 'rotate(180deg)' }} />
              )}
            </BasicIconButton>
          </>
        )}
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'end' }}>
          <div className='d-flex' style={{ width: '100%', justifyContent: 'center' }}>
            <img src={images[index]} alt='' style={{ objectFit: 'contain', height: windowHeight - 48, maxWidth: '100%' }} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageDialog;
